import React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  compose,
  setPropTypes,
  withState,
  lifecycle,
  withHandlers
} from 'recompose';
import { withRouter } from 'react-router';
import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import { Text } from 'recharts';
import TextField from '@material-ui/core/TextField';
import Drawer from '@material-ui/core/Drawer';
import Header from '../../components/Header';
import MenuDrawer from '../../components/MenuDrawer';
import { isAuthenticated, sendLogin, changePassword } from '../../auth/Auth';
import { getAccounts, editAccountStatus } from '../../actions/Accounts';
import ValidationText from '../../components/atoms/ValidationText';
import RequiredButton from '../../components/atoms/RequiredButton';
import ShopChangeButton from '../../components/atoms/ShopChangeButton';
import ProgressDialog from '../../components/molecules/ProgressDialog';
import MenuBox from '../../components/MenuBox';

const styles = theme => ({
  root: {
    backgroundColor: '#F7F7F7',
    flexGrow: 1,
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      maxWidth: 375,
      margin: '0 auto'
    }
  },
  pageTitleDiv: {
    paddingLeft: 15,
    paddingTop: 20,
    paddingBottom: 10,
    textAlign: 'left',
    backgroundColor: '#F7F7F7'
  },
  requiredBtnDiv: {
    marginTop: 25
  },
  textTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    lineHeight: '28px'
  },
  textNote: {
    fontSize: 12,
    fontWeight: 500,
    lineHeight: '18px',
    color: '#333'
  },
  mainPaper: {
    position: 'relative',
    margin: '15px 15px 40px 15px'
  },
  zoom: {
    position: 'relative',
    left: -10,
    top: 20
  },
  searchForm: {
    padding: 20
  },
  textField: {
    padding: '0px 5px 0px 5px'
  },
  button: {
    margin: '5px 5px'
  },
  horizotalLine: {
    borderBottom: '1px solid #dddddd'
  }
});

const propTypes = {
  classes: PropTypes.object.isRequired
};

const enhance = compose(
  connect(
    state => ({
      account: state.accounts.account
    }),
    dispatch => ({
      actions: bindActionCreators({ getAccounts, editAccountStatus }, dispatch)
    })
  ),
  setPropTypes(propTypes),
  withState('openDrawer', 'updateOpenDrawer', false),
  withState('openChangeProgress', 'updateOpenChangeProgress', false),
  withState('loading', 'updateLoading', false),
  withState('isChangeSuccess', 'updateIsChangeSuccess', false),
  withState('resultMessage', 'setResultMessage', ''),
  withState('redirectPath', 'setRedirectPath', '/shops_shop'),
  withState('oldPassword', 'setOldPassword', ''),
  withState('password', 'setPassword', ''),
  withState('passwordError', 'setPasswordError', ''),
  withState('confirmPassword', 'setConfirmPassword', ''),
  withState('confirmPasswordError', 'setConfirmPasswordError', ''),
  withState('phone', 'setPhone', ''),
  withState('phoneAuthCode', 'setPhoneAuthCode', ''),
  withHandlers({
    checkSession: props => data => {
      if (data.error === true && data.payload.status === 403) {
        sendLogin(props);
      }
      return data;
    },
    handleRedirect: props => path => {
      props.history.push(path);
    }
  }),
  lifecycle({
    componentDidMount() {
      isAuthenticated(this.props);
      this.props.actions.getAccounts({ cognitoUserId: '' }).then(data => {
        this.props.checkSession(data);
      });
    }
  })
);

const Main = enhance(
  ({
    classes,
    actions,
    checkSession,
    handleRedirect,
    account,
    openDrawer,
    updateOpenDrawer,
    openChangeProgress,
    updateOpenChangeProgress,
    loading,
    updateLoading,
    isChangeSuccess,
    updateIsChangeSuccess,
    resultMessage,
    setResultMessage,
    redirectPath,
    setRedirectPath,
    oldPassword,
    setOldPassword,
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    passwordError,
    setPasswordError,
    confirmPasswordError,
    setConfirmPasswordError
  }) => {
    const isShop = typeof account !== 'undefined' && account.role === 'SHOP';
    const isReleased =
      account && account.accountStatus && account.accountStatus.status === true;

    // check if is account shop
    if (!(isShop && isReleased)) {
      handleRedirect('/shops');
    }

    const errors = {
      passwordError,
      confirmPasswordError
    };
    const validPassword = () => {
      const value = password;
      if (value === '') {
        setPasswordError('必須項目です。');
        return 1;
      }
      if (
        value.length < 8 ||
        !/[A-Z]/.test(value) ||
        !/[a-z]/.test(value) ||
        !/[0-9]/.test(value)
      ) {
        setPasswordError(
          '英大文字・英小文字・数字全てを含め、8文字以上で入力してください。'
        );
        return 1;
      }
      setPasswordError('');
      setPassword(value);
      return 0;
    };
    const validConfirmPassword = () => {
      const value = password;
      const valueConfirm = confirmPassword;
      if (valueConfirm === '') {
        setConfirmPasswordError('必須項目です。');
        return 1;
      }
      if (value !== valueConfirm) {
        setConfirmPasswordError('パスワードが一致していません。');
        return 1;
      }
      setConfirmPasswordError('');
      setConfirmPassword(value);
      return 0;
    };
    const valid = () => {
      const errorCount = validPassword() + validConfirmPassword();
      if (errorCount === 0) {
        return true;
      }
      return false;
    };

    const changeAction = async () => {
      if (!valid()) {
        alert('登録情報を正しく入力してください。');
      } else {
        updateLoading(true);
        updateOpenChangeProgress(true);
        const accountData = await actions
          .getAccounts({ cognitoUserId: '' })
          .then(data => checkSession(data));
        if (account.role === 'GROUP') {
          setRedirectPath('/groups_group');
        } else if (isReleased !== null && !isReleased) {
          setRedirectPath('/release_step');
        }
        await changePassword(oldPassword, password)
          .then(async () => {
            const params = accountData.payload;
            params.status = 'AVAILABLE';
            const editedStatus = await actions.editAccountStatus(
              params.manageAccountId,
              params
            );
            if (editedStatus.type === 'EDIT_ACCOUNT_STATUS_SUCCESS') {
              updateIsChangeSuccess(true);
              setResultMessage('パスワードを変更しました。');
              updateLoading(false);
            } else {
              updateIsChangeSuccess(false);
              setResultMessage('パスワード変更に失敗しました。');
              updateLoading(false);
            }
          })
          .catch(() => {
            updateIsChangeSuccess(false);
            setResultMessage('パスワード変更に失敗しました。');
            updateLoading(false);
          });
      }
    };

    const progressOkAction = () => {
      if (isChangeSuccess) {
        updateOpenChangeProgress(false);
        handleRedirect(redirectPath);
      } else {
        updateOpenChangeProgress(false);
      }
    };

    return (
      <div className={classes.root}>
        <Header
          onMenuClick={() => {
            updateOpenDrawer(true);
          }}
        />
        <Drawer
          open={openDrawer}
          anchor="left"
          onClose={() => {
            updateOpenDrawer(false);
          }}
        >
          <div tabIndex={0} role="button">
            <MenuDrawer account={account} />
          </div>
        </Drawer>
        <div className={classes.pageTitleDiv}>
          <Typography className={classes.textTitle}>パスワード変更</Typography>
        </div>
        <Paper className={classes.mainPaper} elevation={1}>
          <div className={classes.searchForm}>
            <div>
              <RequiredButton />
            </div>
            <div>
              <TextField
                required
                label="旧パスワード"
                type="password"
                fullWidth
                className={classes.textField}
                margin="normal"
                onChange={e => {
                  setOldPassword(e.target.value);
                }}
              />
            </div>
            <div className={classes.requiredBtnDiv}>
              <RequiredButton />
            </div>
            <div>
              <TextField
                required
                label="新パスワード"
                type="password"
                fullWidth
                className={classes.textField}
                margin="normal"
                onChange={e => {
                  setPassword(e.target.value);
                }}
                onBlur={validPassword}
              />
              <ValidationText message={errors.passwordError} />
            </div>
            <Text className={classes.textNote}>
              パスワードは英大文字・英小文字・数字の３種類全てを含め、8文字以上で設定してください。
            </Text>
            <div className={classes.requiredBtnDiv}>
              <RequiredButton />
            </div>
            <div>
              <TextField
                required
                label="確認用パスワード"
                type="password"
                fullWidth
                className={classes.textField}
                margin="normal"
                onChange={e => {
                  setConfirmPassword(e.target.value);
                }}
                onBlur={validConfirmPassword}
              />
              <ValidationText message={errors.confirmPasswordError} />
            </div>
          </div>
          <div className={classes.horizotalLine} />
          <div className={classes.searchForm}>
            <ShopChangeButton onClickAction={changeAction} />
          </div>
          <ProgressDialog
            openProgress={openChangeProgress}
            progressOkAction={progressOkAction}
            loading={loading}
            resultMessage={resultMessage}
          />
        </Paper>
        <MenuBox account={account} />
      </div>
    );
  }
);

export default withRouter(withStyles(styles)(Main));
